// ============================================================================
// Combat PRO ownership + membership tier helper (VISINVOKE dashboard/account).
//
// SINGLE SOURCE OF TRUTH for all tier values. Every surface (dashboard, pricing
// page, membership page, comparison tables, current-plan
// card, tier cards) reads from TIER_SPECS / SPEC_ROWS below. To change a number,
// edit it ONCE here.
//
// Combat PRO BASE ownership ($49.99 one-time) uses the REAL backend entitlement
// `combat_system_pro` where available. Membership TIERS (Tier 1/2/3) are
// placeholder / local-state only until Stripe is wired. Cloud Sync is bundled
// into Tier 1+ and is no longer a standalone product. Nothing here touches the
// Combat PRO system itself.
// ============================================================================

export const COMBAT_PRO_BASE_PRICE = '49.99';

export const COMBAT_PRO_BASE_INCLUDES = [
  '469 Monsters',
  '319 Spells',
  'Monster Studio',
  'Templates',
  '2 Saved Encounters (cloud-backed)',
  'Encounter System',
];

export const COMBAT_TIERS = {
  none:  { key: 'none',  name: 'No Membership', price: 0,     per: '',       color: '#6b7280' },
  tier1: { key: 'tier1', name: 'Combat PRO Cloud 1', price: 3.99, per: '/month', color: '#10b981' },
  tier2: { key: 'tier2', name: 'Combat PRO Cloud 2', price: 5.99, per: '/month', color: '#8b5cf6' },
  tier3: { key: 'tier3', name: 'Combat PRO Cloud 3', price: 7.99, per: '/month', color: '#f59e0b' },
};

export const TIER_ORDER = ['none', 'tier1', 'tier2', 'tier3'];

// ── Full per-tier specification (the ONLY place these values live) ──
// Memberships are convenience + expansion, NOT gameplay gating. The only
// per-tier limit is the number of saved encounters. Generators are the
// headline premium add-on. Future Features use wording, never quantities.
export const TIER_SPECS = {
  none:  { savedEncounters: '2 total',       cloudSaves: false, crossDevice: false, monsterCloud: false, spellCloud: false },
  tier1: { savedEncounters: '6/family (18)',  cloudSaves: true,  crossDevice: true,  monsterCloud: true,  spellCloud: true  },
  tier2: { savedEncounters: '8/family (24)',  cloudSaves: true,  crossDevice: true,  monsterCloud: true,  spellCloud: true  },
  tier3: { savedEncounters: '12/family (36)', cloudSaves: true,  crossDevice: true,  monsterCloud: true,  spellCloud: true  },
};

// ── Row metadata: label + key into TIER_SPECS. Drives every card + table. ──
export const SPEC_ROWS = [
  { key: 'savedEncounters', label: 'Prepared Encounters' },
  { key: 'cloudSaves',      label: 'Cloud Saves' },
  { key: 'crossDevice',     label: 'Cross Device' },
  { key: 'monsterCloud',    label: 'Monster Cloud' },
  { key: 'spellCloud',      label: 'Spell Cloud' },
];

// Comparison table rows are DERIVED from the single source above.
export const COMPARISON_ROWS = SPEC_ROWS.map((r) => ({
  label: r.label,
  vals: {
    none:  TIER_SPECS.none[r.key],
    tier1: TIER_SPECS.tier1[r.key],
    tier2: TIER_SPECS.tier2[r.key],
    tier3: TIER_SPECS.tier3[r.key],
  },
}));

export function getTierSpec(tierKey) {
  return TIER_SPECS[tierKey] || TIER_SPECS.none;
}

const LS = {
  tier:         'visinvoke_combatpro_tier',
  purchaseDate: 'visinvoke_combatpro_purchase_date',
  history:      'visinvoke_combatpro_purchase_history',
  stats:        'visinvoke_combatpro_stats',
  ownedLocal:   'visinvoke_combatpro_owned_local',
};

function read(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v == null ? fallback : JSON.parse(v);
  } catch (e) { return fallback; }
}
function write(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch (e) { /* ignore */ }
}

// ── Account state accessors (required names: combatProOwned,
//    combatProPurchaseDate, combatProTier, combatProCloudActive,
//    combatProPurchaseHistory, combatProStats) ──
export function getCombatProTier() {
  const t = read(LS.tier, 'none');
  return COMBAT_TIERS[t] ? t : 'none';
}
export function setCombatProTier(t) {
  if (COMBAT_TIERS[t]) { write(LS.tier, t); notify(); }
}
export function getCombatProCloudActive() {
  return getCombatProTier() !== 'none';
}
export function getCombatProPurchaseDate() {
  return read(LS.purchaseDate, null);
}
export function getCombatProPurchaseHistory() {
  const h = read(LS.history, []);
  return Array.isArray(h) ? h : [];
}
export function getCombatProStats() {
  return Object.assign(
    { customMonsters: 0, templates: 0, snapshots: 0, savedEncounters: 0, versionHistory: 0, mostUsedMonster: '\u2014' },
    read(LS.stats, {})
  );
}
// Ownership prefers the real entitlement; falls back to local placeholder.
export function getCombatProOwned(entitlement) {
  if (entitlement === true) return true;
  return read(LS.ownedLocal, false) === true;
}

function pushHistory(entry) {
  const h = getCombatProPurchaseHistory();
  h.unshift(entry);
  write(LS.history, h);
}

// ── Change subscription (lets the dashboard + pages re-render in sync) ──
const listeners = new Set();
export function onCombatProChange(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
function notify() {
  listeners.forEach((fn) => { try { fn(); } catch (e) { /* ignore */ } });
  try { window.dispatchEvent(new Event('combatpro-tier-change')); } catch (e) { /* ignore */ }
}

// ── Placeholder actions (NO Stripe, NO backend, local state only) ──
export function viPurchaseCombatPro() {
  write(LS.ownedLocal, true);
  if (!getCombatProPurchaseDate()) write(LS.purchaseDate, Date.now());
  pushHistory({ date: Date.now(), product: 'Combat PRO', price: '$' + COMBAT_PRO_BASE_PRICE, status: 'Owned (placeholder)', type: 'One-time' });
  notify();
}
export function viUpgradeCombatTier(tierKey) {
  if (!COMBAT_TIERS[tierKey] || tierKey === 'none') return;
  setCombatProTier(tierKey);
  const d = COMBAT_TIERS[tierKey];
  pushHistory({ date: Date.now(), product: 'Membership ' + d.name, price: '$' + d.price.toFixed(2) + d.per, status: 'Active (placeholder)', type: 'Subscription' });
  notify();
}
export function viDowngradeCombatTier(tierKey) {
  if (!COMBAT_TIERS[tierKey]) return;
  setCombatProTier(tierKey);
  const d = COMBAT_TIERS[tierKey];
  if (tierKey === 'none') {
    pushHistory({ date: Date.now(), product: 'Membership cancelled', price: '\u2014', status: 'Cancelled (placeholder)', type: 'Subscription' });
  } else {
    pushHistory({ date: Date.now(), product: 'Membership ' + d.name, price: '$' + d.price.toFixed(2) + d.per, status: 'Active (placeholder)', type: 'Subscription' });
  }
  notify();
}
export function viOpenCombatPricing(navigate) {
  if (navigate) navigate('/dashboard/battletracker/pricing'); else window.location.assign('/dashboard/battletracker/pricing');
}
export function viOpenCombatMembership(navigate) {
  if (navigate) navigate('/dashboard/battletracker/membership'); else window.location.assign('/dashboard/battletracker/membership');
}
export function viOpenCombatPurchaseHistory(navigate) {
  if (navigate) navigate('/dashboard/battletracker/purchase-history'); else window.location.assign('/dashboard/battletracker/purchase-history');
}
// viOpenCombatTools was removed on 8 Sep 2026. It routed from Combat PRO into the standalone
// generator catalogue, which is a separate VISINVOKE product family under Game Systems. Combat PRO
// no longer presents, links to, or counts standalone generators.

// ── Small helpers ──
export function formatTierPrice(tierKey) {
  const d = COMBAT_TIERS[tierKey];
  if (!d || !d.price) return 'Free';
  return '$' + d.price.toFixed(2) + d.per;
}
export function tierRank(t) {
  const i = TIER_ORDER.indexOf(t);
  return i < 0 ? 0 : i;
}
export function fmtDate(ts) {
  if (!ts) return '\u2014';
  try { return new Date(ts).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }); } catch (e) { return '\u2014'; }
}
